import 'cordova-plugin-purchase';

import axios from 'axios';
import { defineStore } from 'pinia';

import { useAuthStore } from '@/store/auth';
import { AvailableSubscription, Product } from '@/types/purchase';

import { useOnboardingStore } from './onboarding';

type State = {
  CdvPurchaseStore: undefined | InstanceType<typeof window.CdvPurchase.Store>;
  isIAPWorking: boolean;
  remainingQuantity: number;
  publicIds: string[];
  availableSubscriptions: AvailableSubscription[];
  products: Product[];
  loadingPaywall: boolean;
};

export const usePurchaseStore = defineStore('purchase', {
  persist: true,
  state: (): State => ({
    CdvPurchaseStore: undefined,
    isIAPWorking: false,
    remainingQuantity: 0,
    publicIds: [],
    availableSubscriptions: [
      {
        id: 'beconfident_subs_89.99_year',
        type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        // It's not necessary to register Play Store subscriptions, we already map (App.vue) the subscriptions based on the platform
        platform: CdvPurchase.Platform.APPLE_APPSTORE,
      },
    ],
    products: [],
    loadingPaywall: false,
  }),
  actions: {
    // todo:types
    async fetchUnconsumed(params?: any) {
      const authStore = useAuthStore();
      const isAuthenticated = Boolean(authStore.token);

      if (!isAuthenticated) {
        return;
      }

      const { data } = await axios.get('/app/purchases/unconsumed', { params });
      this.setRemainingQuantity(data.remainingQuantity);
      this.setPublicIds(data.publicIds);

      return data;
    },
    async create(
      params: Parameters<typeof this.$apiClient.createIAPPurchase>[0],
    ) {
      const authStore = useAuthStore();
      const onboardingStore = useOnboardingStore();

      const data = await this.$apiClient.createIAPPurchase(params);

      await authStore.getMe();

      onboardingStore.setOnboarding({
        ...onboardingStore.onboarding,
        ...data,
      });

      onboardingStore.setPurchaseId(data.purchaseId);

      return data;
    },
    setCdvPurchaseStore(
      CdvPurchaseStore: InstanceType<typeof window.CdvPurchase.Store>,
    ) {
      this.CdvPurchaseStore = CdvPurchaseStore;
    },
    setIsIAPWorking(isIAPWorking: boolean) {
      this.isIAPWorking = isIAPWorking;
    },
    setAvailableSubscriptions(availableSubscriptions: AvailableSubscription[]) {
      this.availableSubscriptions = availableSubscriptions;
    },
    setRemainingQuantity(remainingQuantity: number) {
      this.remainingQuantity = remainingQuantity;
    },
    setPublicIds(publicIds: string[]) {
      this.publicIds = publicIds;
    },
    setProducts(products: Product[]) {
      this.products = products;
    },
    setLoadingPaywall(loadingPaywall: boolean) {
      this.loadingPaywall = loadingPaywall;
    },
  },
  getters: {},
});
